import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import CircularProgress from '@mui/material/CircularProgress'
import { memo, useRef, useEffect, useState } from 'react'
import { useVideoBanners } from '@/core/hooks'
import { isYouTubeUrl, toYouTubeEmbedUrl } from '@/core/services/videoBanner.service'

const HeroSection = memo(() => {
    const videoRef = useRef<HTMLVideoElement>(null)
    const hasLoadedRef = useRef(false)
    const [isVideoReady, setIsVideoReady] = useState(false)
    const { heroBanner, isLoading: isLoadingBanner } = useVideoBanners()

    const hasCover = !!heroBanner?.cover
    const hasVdo = !!heroBanner?.vdo
    const isExternal = !!heroBanner?.isExternal
    const externalLink = heroBanner?.link ?? ''
    const isYouTube = hasVdo && !isExternal && isYouTubeUrl(heroBanner!.vdo)
    const embedUrl = isYouTube ? toYouTubeEmbedUrl(heroBanner!.vdo) : ''

    useEffect(() => {
        if (videoRef.current && hasVdo && !isYouTube && !hasLoadedRef.current) {
            hasLoadedRef.current = true
            videoRef.current.src = heroBanner!.vdo
            videoRef.current.load()
        }
    }, [heroBanner, hasVdo, isYouTube])

    const handleVideoReady = () => {
        setTimeout(() => setIsVideoReady(true), 1000)
    }

    const showLoader = isLoadingBanner || (!isYouTube && !isExternal && hasVdo && !isVideoReady)

    return (
        <Stack position={'relative'} width="100%" sx={{ aspectRatio: '16/9' }} overflow={'hidden'}>
            {showLoader && (
                <Stack
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    alignItems="center"
                    justifyContent="center"
                    zIndex={1}>
                    <Skeleton
                        variant="rectangular"
                        width="100%"
                        height="100%"
                        animation="pulse"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            bgcolor: '#ccdeef',
                            '&::after': {
                                background: 'linear-gradient(90deg, transparent, #b3d5f1, transparent)'
                            }
                        }}
                    />
                    <CircularProgress sx={{ zIndex: 2 }} />
                </Stack>
            )}

            {heroBanner && (
                <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                    {/* Cover image (fallback when no vdo, or when isExternal) */}
                    {hasCover && (!hasVdo || isExternal) && (
                        <Box component="img" src={heroBanner.cover} width="100%" height="100%" sx={{ objectFit: 'cover', display: 'block' }} />
                    )}

                    {/* YouTube iframe — autoplay (ไม่แสดงเมื่อ isExternal) */}
                    {isYouTube && (
                        <Box
                            component="iframe"
                            src={embedUrl}
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                border: 'none'
                            }}
                            allow="autoplay; encrypted-media"
                            allowFullScreen
                        />
                    )}

                    {/* Native video — autoplay (ไม่แสดงเมื่อ isExternal) */}
                    {hasVdo && !isYouTube && !isExternal && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                opacity: isVideoReady ? 1 : 0,
                                transition: 'opacity 0.3s ease-in-out'
                            }}>
                            <video
                                ref={videoRef}
                                width="100%"
                                height="100%"
                                muted
                                autoPlay
                                loop
                                playsInline
                                preload="auto"
                                style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                                onCanPlay={handleVideoReady}
                            />
                        </Box>
                    )}

                    {/* External link overlay — คลิกทั้ง banner เพื่อเปิด link ใหม่ */}
                    {isExternal && externalLink && (
                        <Box
                            component="a"
                            href={externalLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'block',
                                cursor: 'pointer'
                            }}
                        />
                    )}
                </Box>
            )}
        </Stack>
    )
})

HeroSection.displayName = 'HeroSection'

export default HeroSection
